"use client";

import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";

export type AppSidebarOverride = {
  content: ReactNode;
  footer?: ReactNode;
};

type AppSidebarOverrideContextValue = {
  sidebarOverride: AppSidebarOverride | null;
  setSidebarOverride: (override: AppSidebarOverride | null) => void;
};

const AppSidebarOverrideContext =
  createContext<AppSidebarOverrideContextValue | null>(null);

export function AppSidebarOverrideProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [sidebarOverride, setSidebarOverride] =
    useState<AppSidebarOverride | null>(null);
  const value = useMemo(
    () => ({
      sidebarOverride,
      setSidebarOverride,
    }),
    [sidebarOverride]
  );

  return (
    <AppSidebarOverrideContext.Provider value={value}>
      {children}
    </AppSidebarOverrideContext.Provider>
  );
}

export function useAppSidebarOverride() {
  const context = useContext(AppSidebarOverrideContext);
  if (!context) {
    return {
      sidebarOverride: null,
      setSidebarOverride: () => undefined,
    };
  }

  return context;
}

export function AppSidebarOverrideContent({
  fallback,
}: {
  fallback: ReactNode;
}) {
  const { sidebarOverride } = useAppSidebarOverride();
  return sidebarOverride?.content ?? fallback;
}

export function AppSidebarOverrideFooter({
  fallback,
}: {
  fallback: ReactNode;
}) {
  const { sidebarOverride } = useAppSidebarOverride();
  const footer =
    sidebarOverride?.footer === undefined ? fallback : sidebarOverride.footer;

  return footer;
}
